export class ModalManager {
  constructor() {
    this.container = null;
    this.activeModals = [];
    this.init();
  }

  init() {
    // 1. Create or locate the root overlay container
    this.container = document.getElementById("modal-overlay-root");
    if (!this.container) {
      this.container = document.createElement("div");
      this.container.id = "modal-overlay-root";
      this.container.className = "modal-overlay-root hidden";
      document.body.appendChild(this.container);
    }

    // 2. Global keyboard listeners (Close top modal on Escape)
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.activeModals.length > 0) {
        const topModal = this.activeModals[this.activeModals.length - 1];
        if (topModal.options.closeOnEscape !== false) {
          this.close(topModal.id);
        }
      }
    });

    // 3. Backdrop click to close top modal
    this.container.addEventListener("click", (e) => {
      if (e.target === this.container && this.activeModals.length > 0) {
        const topModal = this.activeModals[this.activeModals.length - 1];
        if (topModal.options.closeOnBackdrop !== false) {
          this.close(topModal.id);
        }
      }
    });
  }

  /**
   * Open a custom modal
   * @param {Object} config - { id, title, content, buttons, closeOnEscape, closeOnBackdrop }
   */
  open({ id = `modal-${Date.now()}`, title = "", content = "", buttons = [], ...options }) {
    // Prevent duplicate modal IDs
    if (this.activeModals.some(m => m.id === id)) return;

    // Create modal element
    const modalEl = document.createElement("div");
    modalEl.className = "modal-window";
    modalEl.dataset.modalId = id;

    // Render HTML structure
    modalEl.innerHTML = `
      <div class="modal-header">
        <h2 class="modal-title">${title}</h2>
        <button class="modal-close-btn" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body"></div>
      ${buttons.length > 0 ? `<div class="modal-footer"></div>` : ""}
    `;

    // Populate body (handles both string content and DOM nodes)
    const bodyEl = modalEl.querySelector(".modal-body");
    if (typeof content === "string") {
      bodyEl.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      bodyEl.appendChild(content);
    }

    // Attach Close Button listener
    modalEl.querySelector(".modal-close-btn").addEventListener("click", () => {
      this.close(id);
    });

    // Attach Footer Buttons
    if (buttons.length > 0) {
      const footerEl = modalEl.querySelector(".modal-footer");
      buttons.forEach((btnConfig) => {
        const btn = document.createElement("button");
        btn.className = `modal-btn ${btnConfig.class || "secondary-btn"}`;
        btn.textContent = btnConfig.label || "OK";
        btn.addEventListener("click", () => {
          if (btnConfig.onClick) btnConfig.onClick(this, id);
          if (btnConfig.closeOnClick !== false) this.close(id);
        });
        footerEl.appendChild(btn);
      });
    }

    // Show container & append modal
    this.container.classList.remove("hidden");
    this.container.appendChild(modalEl);

    // Track active modal
    this.activeModals.push({ id, element: modalEl, options });
    return id;
  }

  /**
   * Close a specific modal by ID (or the top modal if no ID provided)
   */
  close(id) {
    const targetId = id || (this.activeModals.length > 0 && this.activeModals[this.activeModals.length - 1].id);
    if (!targetId) return;

    const modalIndex = this.activeModals.findIndex(m => m.id === targetId);
    if (modalIndex === -1) return;

    const [modalData] = this.activeModals.splice(modalIndex, 1);
    modalData.element.remove();

    // Hide backdrop if no modals remain
    if (this.activeModals.length === 0) {
      this.container.classList.add("hidden");
    }
  }

  /**
   * Helper: Open a confirmation dialog returning a Promise
   */
  confirm(title, message) {
    return new Promise((resolve) => {
      this.open({
        title,
        content: `<p>${message}</p>`,
        closeOnBackdrop: false,
        buttons: [
          {
            label: "Cancel",
            class: "secondary-btn",
            onClick: () => resolve(false)
          },
          {
            label: "Confirm",
            class: "danger-btn",
            onClick: () => resolve(true)
          }
        ]
      });
    });
  }
}

export const modalManager = new ModalManager();